import { jsx } from 'react/jsx-runtime';
import { forwardRef, useContext, useLayoutEffect } from 'react';
import { FloatingPortal, FloatingOverlay, FloatingFocusManager } from '@floating-ui/react';
import clsx from 'clsx';
import { EventShield } from '../event-shield/event-shield.js';
import { useIdProvider } from '../hooks/id-provider.js';
import { PREVENT_WHEEL_CAPTURE, PREVENT_KEY_CAPTURE, PREVENT_EVENT_CAPTURE } from '../utils/event.js';
import { LastInteraction } from '../utils/interaction.js';
import { usePopupOrdering } from '../utils/persistent-popup-hooks.js';
import { PersistentPopupOutlet } from '../utils/persistent-popup-outlet.js';
import { PopoverOutletContext, PopoverOutlet } from '../utils/popover-outlet.js';
import { disablePageScroll } from '../utils/scroll.js';
import { positionAbsolute } from '../styles/composables.css.js';
import { scrim } from './popover-primitive.css.js';

/**
 * Floating popover container
 *
 * @see https://fpl.figma.design/primitives/popover/
 */
const Container = forwardRef(({ isOpen, type, children, context, allowScrimPassthrough = false, useSoftDismiss, style, config2025CuratorHacks, className, ...props }, ref) => {
    const rootRef = useContext(PopoverOutletContext);
    /**
     * When a popover is an inert type, it behaves like a modal where we lock scroll and render the background inert.
     * We also want to swallow certain events, particularly arrow as these shouldn't bubble to the canvas.
     */
    const isInertType = type === 'listbox' || type === 'menu';
    const [id, Provider] = useIdProvider();
    // These come from z-index.json
    const zIndex = {
        dialog: 'var(--z-index-window)',
        grid: 'var(--z-index-window)',
        tree: 'var(--z-index-window)',
        listbox: 'var(--z-index-dropdown)',
        menu: config2025CuratorHacks ? '9' : 'var(--z-index-dropdown)',
        tutorial: 'var(--z-index-tooltip)',
        tooltip: 'var(--z-index-tooltip)',
    };
    useLayoutEffect(() => {
        if (!isOpen || !useSoftDismiss)
            return;
        // Note: we only want to run this when we render the FloatingOverlay component
        return disablePageScroll();
    }, [isOpen, useSoftDismiss]);
    const newStackingProps = usePopupOrdering({
        ...props,
        ...PREVENT_EVENT_CAPTURE,
        ...PREVENT_KEY_CAPTURE,
        ...PREVENT_WHEEL_CAPTURE,
        style,
        className: clsx(className, positionAbsolute),
    }, {
        popupClosed: !isOpen,
    });
    // eslint-disable-next-line fpl/fpl-data-attr -- hidden
    if (!isOpen && type === 'listbox')
        return jsx("div", { style: { display: 'none' }, children: children });
    else if (!isOpen)
        return null;
    if (useSoftDismiss) {
        // TODO: we should revisit this as we look into removing floating UI.
        // Only return focus if opened via keyboard to avoid unexpected focus changes for mouse users.
        const shouldReturnFocus = LastInteraction.type === 'keyboard' ? undefined : false;
        return (jsx(EventShield, { display: "contents", eventListeners: isInertType ? ['onKeyDown', 'onKeyUp', 'onKeyPress'] : [], children: jsx(FloatingPortal, { root: rootRef, children: jsx(FloatingOverlay, { ...(!allowScrimPassthrough
                        ? { ...PREVENT_EVENT_CAPTURE, ...PREVENT_KEY_CAPTURE, ...PREVENT_WHEEL_CAPTURE }
                        : {}), className: allowScrimPassthrough ? undefined : scrim, style: { zIndex: zIndex[type], isolation: 'isolate' }, "data-fpl-floating-overlay": true, children: jsx(PopoverOutlet, { children: jsx(FloatingFocusManager, { context: context, modal: isInertType, initialFocus: type === 'tooltip' ? -1 : 0, returnFocus: shouldReturnFocus, children: jsx("div", { "data-fpl-component": "primitive", ref: ref, style: style, ...PREVENT_EVENT_CAPTURE, ...PREVENT_KEY_CAPTURE, ...PREVENT_WHEEL_CAPTURE, className: className, ...props, children: children }) }) }) }) }) }));
    }
    if (type !== 'menu') {
        return (jsx(Provider, { value: id, children: jsx(FloatingPortal, { root: rootRef, children: jsx(PersistentPopupOutlet, { children: jsx("section", { "data-fpl-component": "primitive", "aria-labelledby": id, ref: ref, ...newStackingProps, children: children }) }) }) }));
    }
    return (jsx(Provider, { value: id, children: jsx(FloatingPortal, { root: rootRef, children: jsx(PopoverOutlet, { children: jsx("section", { "data-fpl-component": "primitive", ...PREVENT_EVENT_CAPTURE, ...PREVENT_KEY_CAPTURE, ...PREVENT_WHEEL_CAPTURE, "aria-labelledby": id, ref: ref, ...props, style: { ...style, zIndex: zIndex[type] }, className: className, children: children }) }) }) }));
});
Container.displayName = 'PopoverPrimitive.Container';

export { Container };
//# sourceMappingURL=popover-primitive.container.js.map
